/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import type { Mention, Registre } from '@/types';
import { RANGS_PARENTS_MARIAGE, cleEtatMatrimonialParents } from '@/constants';

import {
  formatAdressePrefixee,
  formatCoordonneesRegistre,
  formatDate,
  formatDateNaissance,
  formatLieuActe,
  formatLieuIndividu,
  formatMentions,
  formatParent,
  formatProfession,
  formatSexe,
} from './formatters';

/** Bloc d'un conjoint, tel que l'attend `ActeNumMariageController`. */
const formatConjoint = (conjoint: Record<string, any> | undefined) => ({
  nomFamille: conjoint?.nomFamille,
  nomUsage: conjoint?.nomUsage,
  prenom: conjoint?.prenom,
  prenomsComplementaires: conjoint?.prenomsComplementaires,
  naissanceDate: formatDateNaissance(conjoint ?? {}),
  ...formatLieuIndividu(conjoint?.adresseNaissance),
  ...formatAdressePrefixee(conjoint?.adresseDomicile, 'domicile'),
  profession: formatProfession(conjoint?.profession),
  ...(!_.isNil(formatSexe(conjoint?.sexe)) && {
    sexe: formatSexe(conjoint?.sexe),
  }),
  /*
   * Situation ANTÉRIEURE au mariage, portée par `ec_actm` mais saisie ici : le
   * contrat suit le domaine, le repository fait le partage
   * (`extraireEtatAnterieur`). Le back purge ce que la situation n'autorise pas,
   * il n'y a donc rien à filtrer ici.
   *
   * ⚠️ Pas de sexe pour l'ex-conjoint : `ec_actm` n'en a pas de colonne,
   * contrairement à l'acte de décès.
   */
  etatMatrimonialAnterieureCode: conjoint?.etatMatrimonialAnterieureCode,
  dateEtatMatrimonial: formatDate(conjoint?.dateEtatMatrimonial),
  nomExConjoint: conjoint?.nomExConjoint,
  prenomExConjoint: conjoint?.prenomExConjoint,
});

/**
 * Bloc du parent 1 d'un conjoint, porteur de l'état matrimonial du COUPLE.
 *
 * ⚠️ L'état matrimonial est joint au parent 1 et non envoyé à part : c'est là
 * que `ActeNumMariageController` le lit. Il est ajouté MÊME si le parent est
 * vide — `formatParent` rend alors `undefined`, et le bloc se réduit au seul
 * état matrimonial, que le back extrait avant de décider s'il crée un individu.
 * L'omettre perdrait une saisie que l'écran présente pourtant.
 */
const formatParent1AvecEtatDuCouple = (
  parent: Record<string, any> | undefined,
  filiationNonRenseignee: boolean | undefined,
  etatMatrimonialParents: string | undefined,
) => ({
  ...formatParent(parent, filiationNonRenseignee),
  ...(!_.isEmpty(etatMatrimonialParents) && { etatMatrimonialParents }),
});

/**
 * Les deux blocs de parents d'un conjoint, aux noms du contrat d'API.
 *
 * ⚠️ C'est ICI que se fait la traduction entre les rangs de `data` — 1-2 pour le
 * premier conjoint, 3-4 pour le second, imposés par le `ParentForm` de la lib
 * qui nomme ses champs `parent{n}.*` — et les noms parlants du contrat
 * (`parent1Conjoint2`…). La table `RANGS_PARENTS_MARIAGE` en est la source, lue
 * aussi par les étapes de saisie et les validators.
 */
const blocsParents = (data: Record<string, any>, numeroConjoint: 1 | 2) => {
  const [rang1, rang2] = RANGS_PARENTS_MARIAGE[`conjoint${numeroConjoint}`];

  /*
   * Un TUPLE et non un objet à clés calculées : celles-ci disparaissent du type
   * du payload une fois étalées, et les quatre blocs de parents cessent d'être
   * visibles pour qui consomme le retour du builder. Les clés restent donc
   * littérales à l'appel.
   */
  return [
    formatParent1AvecEtatDuCouple(
      data?.[`parent${rang1}`],
      data?.[`filiationInconnueParent${rang1}`],
      data?.[cleEtatMatrimonialParents(numeroConjoint)],
    ),
    formatParent(
      data?.[`parent${rang2}`],
      data?.[`filiationInconnueParent${rang2}`],
    ),
  ] as const;
};

/**
 * Construit le payload de `POST /numerisation-actes/mariage`, au contrat de
 * `ActeNumMariageController::storeMariage` :
 * `{ acte, conjoint1, conjoint2, parent1Conjoint1, parent2Conjoint1,
 * parent1Conjoint2, parent2Conjoint2, notaire, mentions }`.
 *
 * ⚠️ Trois répartitions à connaître :
 *
 * 1. **deux lieux distincts** dans `acte` : la commune où l'acte est dressé
 *    (`lieuActe`) et le lieu de la célébration (`lieuMariage`). Le fonds repris
 *    porte toujours deux lignes `ec_acte_adresse` différentes ; les confondre
 *    rendrait les deux colonnes indissociables.
 * 2. **situation antérieure dans le bloc du conjoint**, alors que `ec_actm` la
 *    porte — cf. `formatConjoint`.
 * 3. **état matrimonial des parents dans le bloc du parent 1**, parce qu'il
 *    qualifie le couple et non l'individu — cf. `formatParent1AvecEtatDuCouple`.
 * 4. **les quatre parents sont lus aux rangs 1 à 4 de `data`** et non sous des
 *    noms parlants : cf. `blocsParents`.
 *
 * Le NOTAIRE est le septième individu du dossier (rôle `NOT`) : son adresse est
 * celle de l'office, rangée dans le bloc de domicile de l'individu.
 *
 * Fonction pure, testable sans React.
 */
const buildActeMariagePayload = (
  data: Record<string, any>,
  mentions: Mention[],
  selectedRegistre?: Partial<Registre>,
) => {
  const lieuActe = formatLieuActe(data?.adresseActe);
  const lieuMariage = formatLieuActe(data?.adresseMariage);
  const [parent1Conjoint1, parent2Conjoint1] = blocsParents(data, 1);
  const [parent1Conjoint2, parent2Conjoint2] = blocsParents(data, 2);

  return {
    acte: {
      ...formatCoordonneesRegistre(selectedRegistre, data?.dateActe),
      numeroActe: data?.numeroActe,
      numeroOrdre: data?.ordreActe,
      acteNumeroComplement: data?.complementActe,
      heureDresseActe: data?.heureActe,
      minuteDresseActe: data?.minuteActe,
      identifiantSousTypeActe: data?.sousTypeActe,
      ...(!_.isNil(lieuActe) && { lieuActe }),
      // Officier d'état civil : les trois champs structurés. La phrase rendue
      // (`am_a_sign`) reste au back, qui ne l'écrit pas.
      acteSignataireNomFamille: data?.officierNomFamille,
      acteSignatairePrenoms: data?.officierPrenoms,
      acteSignataireQualite: data?.officierQualite,
      dateMariage: formatDate(data?.dateMariage),
      heureMariage: data?.heureMariage,
      minuteMariage: data?.minuteMariage,
      ...(!_.isNil(lieuMariage) && { lieuMariage }),
      // Contrat de mariage. Le back purge la date si la case est décochée.
      contratMariageExiste: Boolean(data?.contratMariage),
      contratMariageDate: formatDate(data?.dateContratMariage),
    },
    conjoint1: formatConjoint(data?.conjoint1),
    conjoint2: formatConjoint(data?.conjoint2),
    parent1Conjoint1,
    parent2Conjoint1,
    parent1Conjoint2,
    parent2Conjoint2,
    notaire: {
      nomFamille: data?.notaire?.nomFamille,
      prenom: data?.notaire?.prenom,
      prenomsComplementaires: data?.notaire?.prenomsComplementaires,
      ...formatAdressePrefixee(data?.notaire?.adresseDomicile, 'domicile'),
    },
    ...(!_.isEmpty(mentions) && { mentions: formatMentions(mentions) }),
  };
};

export default buildActeMariagePayload;
